import { useId, useState } from 'react'
import { Icon } from '@iconify/react'
import type { DecisionChoice } from '../game'
import { decisionAsset } from '../ui/asset'
import { decisionIconDescriptor } from '../ui/decisionIcons'
import { ClubCrest } from './ClubCrest'
import { DecisionIcon } from './DecisionIcon'

interface DecisionCardProps {
  choice: DecisionChoice
  selected?: boolean
  resolving?: boolean
  awaitingConfirmation?: boolean
  locked?: boolean
  highlightedEffectIndex?: number | null
  resolvedEffectIndex?: number | null
  resolutionPhase?: 'idle' | 'committed' | 'drawing' | 'landing' | 'resolved'
  onChoose: (choice: DecisionChoice) => void
}

const toneIcons = {
  cost: 'mdi:minus-circle-outline',
  gain: 'mdi:plus-circle-outline',
  neutral: 'mdi:information-outline',
  warning: 'mdi:dice-multiple-outline',
} as const

const phaseCopy = {
  committed: 'Decisión bloqueada. Ya no hay vuelta atrás.',
  drawing: 'El desenlace está en juego…',
  landing: 'El veredicto está a punto de caer…',
  resolved: 'Elección completada.',
} as const

export function DecisionCard({ choice, selected = false, resolving = false, awaitingConfirmation = false, locked = false, highlightedEffectIndex = null, resolvedEffectIndex = null, resolutionPhase = 'idle', onChoose }: DecisionCardProps) {
  const [failedArt, setFailedArt] = useState(false)
  const summaryId = useId()
  const riskId = useId()
  const effectsId = useId()
  const requirementId = useId()
  const disabled = choice.disabled || resolving || locked
  const chanceEffects = choice.effects.filter((effect) => effect.metric === 'condition' && effect.value.endsWith('%'))
  const isChanceChoice = chanceEffects.length >= 2
  const resolvedTone = resolvedEffectIndex === null ? null : choice.effects[resolvedEffectIndex]?.tone
  const decisionCategory = decisionIconDescriptor(choice.art).category
  const isRewardChoice = decisionCategory === 'object' || decisionCategory === 'relic'
  const requiredRelic = choice.badge?.match(/^Reliquia requerida · (.+)$/u)?.[1]
  const describedBy = [riskId, requiredRelic ? requirementId : null, isRewardChoice ? null : summaryId, choice.effects.length > 0 ? effectsId : null].filter(Boolean).join(' ')

  return (
    <article className={`decision-card${selected ? ' is-selected' : ''}${choice.disabled ? ' is-disabled' : ''}${resolutionPhase !== 'idle' ? ` is-chance-${resolutionPhase}` : ''}${resolvedTone ? ` is-outcome-${resolvedTone}` : ''}`} data-decision-category={decisionCategory} data-resolution-phase={resolutionPhase}>
      <button
        className="decision-card__hit"
        type="button"
        disabled={disabled}
        aria-describedby={describedBy}
        aria-label={resolving && selected ? `Resolviendo ${choice.title}` : awaitingConfirmation ? `Continuar con el resultado de ${choice.title}` : choice.disabled || locked ? `${choice.title}: no disponible` : `Elegir ${choice.title}`}
        onClick={() => onChoose(choice)}
      />
      <div className="decision-card__art" aria-hidden="true">
        {!failedArt ? (
          <img src={decisionAsset(choice.art)} alt="" loading="eager" onError={() => setFailedArt(true)} />
        ) : (
          <span className="decision-card__fallback"><Icon icon="mdi:soccer-field" /></span>
        )}
        {isChanceChoice && selected && resolutionPhase !== 'idle' && <span className="decision-card__scan" aria-hidden="true" />}
        {choice.clubName && <span className="decision-card__club-crest"><ClubCrest name={choice.clubName} size="large" /></span>}
      </div>
      <DecisionIcon art={choice.art} clubName={choice.clubName} />
      <span id={riskId} className={`decision-card__risk risk--${choice.risk ?? 'low'}`}>
        {choice.risk ? `Riesgo ${choice.risk === 'low' ? 'bajo' : choice.risk === 'medium' ? 'medio' : 'alto'}` : 'Decisión táctica'}
      </span>
      <div className="decision-card__body">
        {requiredRelic ? (
          <span id={requirementId} className="decision-card__requirement" aria-label={`Requiere reliquia: ${requiredRelic}`}>
            <span className="decision-card__requirement-label"><Icon icon="mdi:diamond-stone" aria-hidden="true" /> Requiere reliquia</span>
            <strong className="decision-card__requirement-name">{requiredRelic}</strong>
          </span>
        ) : choice.badge ? <span className="decision-card__type">{choice.badge}</span> : null}
        <h3>{choice.title}</h3>
        {!isRewardChoice && <p id={summaryId}>{choice.summary}</p>}
        {isChanceChoice && selected && resolutionPhase !== 'idle' && (
          <span className="decision-resolution-status" role="status" aria-live="assertive" aria-atomic="true">
            {phaseCopy[resolutionPhase as keyof typeof phaseCopy]} {resolutionPhase === 'resolved' ? 'Revisa la consecuencia y continúa.' : `${chanceEffects.map((effect) => effect.value).join(' · ')} en juego`}
          </span>
        )}
        {choice.effects.length > 0 && (
          <ul id={effectsId} className="decision-card__effects" aria-label="Consecuencias" aria-live="polite">
            {choice.effects.map((effect, index) => (
              <li className={`effect effect--${effect.tone}${highlightedEffectIndex === index ? ' is-drawing' : ''}${resolvedEffectIndex === index ? ' is-resolved' : ''}${resolvedEffectIndex !== null && effect.metric === 'condition' && resolvedEffectIndex !== index ? ' is-not-selected' : ''}`} key={`${effect.metric}-${effect.label}`}>
                <Icon icon={resolvedEffectIndex === index ? 'mdi:check-bold' : toneIcons[effect.tone]} aria-hidden="true" />
                <span className="effect__copy"><span>{effect.label}</span>{effect.when && <small>{effect.when}</small>}</span>
                <strong>{resolvedEffectIndex === index ? 'Resultado' : effect.value}</strong>
              </li>
            ))}
          </ul>
        )}
        <span className="decision-card__action" aria-hidden="true">
          {resolving && selected
            ? resolutionPhase === 'committed' ? 'Decisión fijada'
              : resolutionPhase === 'landing' ? 'Último instante…'
                : 'Resolviendo…'
            : awaitingConfirmation ? 'Continuar' : choice.disabled || locked ? 'No disponible' : 'Elegir esta opción'}
        </span>
      </div>
    </article>
  )
}
